import React, { useEffect, useState } from 'react'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import { useSelector } from 'react-redux'

import { CatalogUniqueChoice } from '../catalog-unique-choice/CatalogUniqueChoice'
import { updateOrderProductIngredientByGroup } from 'shared/utils/ecommerce'
import {
    getIngredientById,
    getIngredientsFromListIngredientsIds
} from 'shared/store/ecommerce/catalog'
import { CatalogStepperIngredients } from '../catalog-stepper-ingredients'
import { filterGroupsOfIngredientsBySingleChoice } from 'shared/utils'

type CatalogComposableStepperProps = {
    order: ?PurchasableItem,
    ingredients: Array<any>,
    onUpdateOrder: (order: ?PurchasableItem) => void,
    sendMeta: any,
    setDisabledButton: (disabled: boolean) => void,
    isFromCart: boolean
}

type SelectedIngredientsType = {
    [key: string]: string
}

export const CatalogComposableStepper = ({
    order,
    ingredients: defaultIngredientsGroup,
    onUpdateOrder,
    sendMeta,
    setDisabledButton,
    isFromCart
}: CatalogComposableStepperProps) => {
    const ecommerceState = useSelector(state => state?.ecommerceCatalogState)
    const [selectedIdIngredients, setSelectedIdIngredients] = useState({})
    const {
        isUnique,
        ingredients: ingredientsGroup
    } = filterGroupsOfIngredientsBySingleChoice(defaultIngredientsGroup)

    useEffect(() => {
        if (order && order.customizedIngredients) {
            if (order.customizedIngredients.length === 0 && isUnique) {
                setDisabledButton(true)
            }
            if (order.customizedIngredients.length !== 0 && isFromCart) {
                const transformedData = order.customizedIngredients.reduce(
                    (acc, item) => {
                        if (item.type && !acc[item.type]) {
                            acc[item.type] = item.selected
                        }
                        return acc
                    },
                    {}
                )
                setSelectedIdIngredients(transformedData)
            }
        }
    }, [isUnique, order, setDisabledButton])

    const updateOrder = (
        currentSelectedIngredients: SelectedIngredientsType
    ) => {
        const selectedIngredients = Object.entries(
            currentSelectedIngredients
        ).map(([key, value]) => {
            //need this to fix flow
            if (typeof value === 'string') {
                const newIngredient = getIngredientById(ecommerceState, value)
                if (newIngredient) {
                    const { name, price, id: currentId } = newIngredient
                    return {
                        initial: currentId,
                        selected: currentId,
                        initialQuantity: 0,
                        quantity: 1,
                        label: name,
                        price,
                        type: key
                    }
                }
            }
        })
        const newOrder = updateOrderProductIngredientByGroup(
            order,
            selectedIngredients
        )
        if (isUnique) {
            setDisabledButton(
                newOrder.customizedIngredients.length ===
                    defaultIngredientsGroup.length
                    ? false
                    : true
            )
        } else {
            setDisabledButton(false)
        }
        onUpdateOrder(newOrder)
    }

    const handleIngredientSelect = (selectedId: string, groupName: string) => {
        if (!!selectedIdIngredients) {
            const updatedSelectedIngredients = {
                ...selectedIdIngredients,
                [groupName]: selectedId
            }
            setSelectedIdIngredients({ ...updatedSelectedIngredients })
            updateOrder({ ...updatedSelectedIngredients })
        }
    }

    return (
        <>
            {isUnique ? (
                getIngredientsFromListIngredientsIds(
                    ecommerceState,
                    ingredientsGroup
                ).map(group => (
                    <CatalogUniqueChoice
                        key={group.groupType}
                        ingredients={group.ingredients}
                        title={group.groupType}
                        groupName={group.groupType}
                        onSelected={handleIngredientSelect}
                        selectedIdIngredient={
                            selectedIdIngredients[group.groupType]
                        }
                    />
                ))
            ) : (
                <CatalogStepperIngredients
                    order={order}
                    listIngredients={defaultIngredientsGroup}
                    dispatchOrder={onUpdateOrder}
                    featureCustoAvailable={true}
                    featureExtraAvailable={true}
                    sendMeta={sendMeta}
                />
            )}
        </>
    )
}
